import type { ColumnDef, SortingState } from "@tanstack/react-table";
import * as React from "react";
import { useLayoutEffect, useMemo, useState } from "react";

import { SimulatorPickActionCell } from "@/components/Reports/SimulatorPickActionCell";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  ConditionAnalysisNode,
  ConditionAnalysisSlice,
} from "../../../../utils/conditionAnalysisSelectors";
import { compareRegimeDrilldownTableKeys } from "../../../../utils/marketContextDrilldownLabels";
import { toNaturalMetricDefinition } from "../../../../utils/marketContextMetricDefinitions";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import { TradeLogSortableCell } from "../../TradeLog/components/tradeLogSortableCell";
import { MarketContextSliceCategoryBadge } from "./MarketContextSliceCategoryBadge";
import {
  getDisplaySliceName,
  getSliceCategoryLabel,
  sliceKindToDrilldownTableKey,
} from "../marketContextSliceDisplay";
import {
  AddToSimulatorColumnHeader,
  fmtInt,
  fmtNum,
  fmtPct,
  fmtPnl,
  MarketContextActionColumnCell,
  MarketContextActionColumnHeader,
  MarketContextMetricColumnHeader,
  MarketContextTooltipContent,
  marketContextPreviewKeyMatches,
} from "./marketContextDataTableShared";
import {
  marketContextActiveRowSelector,
  scheduleScrollMarketContextRow,
} from "../../../../utils/marketContextEvidenceScroll";
import {
  marketContextVariantTooltipText,
  candleDirectionMetricKeyFromSliceKind,
  ensureSentence,
  situationBriefTooltip,
} from "./marketContextVariantTooltipText";
import {
  OPEN_SLICE_ROW_TOOLTIP,
  WIN_RATE_COLUMN_TOOLTIP,
} from "./marketContextTableTooltips";

/** Body rows visible before vertical scroll in Exit Placement explorer (header stays sticky). */
export const MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT = 10;

const MARKET_CONTEXT_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const MARKET_CONTEXT_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500";

export type SliceHeadline = {
  sample?: number;
  winRate?: number;
  totalPnl?: number;
  expectancy?: number;
  bestSituation?: string;
  worstSituation?: string;
};

export type SliceHeadlineFn = (
  slice: ConditionAnalysisSlice,
  options?: { sessionMarginalSourceNode?: ConditionAnalysisNode | null },
) => SliceHeadline;

function normalizeDefKey(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\(~\d+\s*bars\)/gi, "")
    .replace(/\(short dir\)|\(long window\)/gi, "")
    .replace(/\s+/g, "_")
    .replace(/[^\w|+]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function sliceDefinitionTooltip(
  slice: ConditionAnalysisSlice,
  metricDefinitions?: Record<string, unknown>,
  candleTimeframe?: string | null,
): string {
  const defs = metricDefinitions ?? {};
  const label = String(slice.label ?? "").trim();
  const display = getDisplaySliceName(slice, candleTimeframe);
  const tooltipOptions = {
    sliceKind: slice.kind,
    labelText: label || display,
    candleTimeframe,
  };
  const candleMetricFromKind = candleDirectionMetricKeyFromSliceKind(slice.kind);

  if (
    candleMetricFromKind &&
    slice.candle_direction &&
    (slice.kind === "market_direction" ||
      slice.kind === "candle_direction" ||
      slice.kind === "market_direction_short" ||
      slice.kind === "candle_direction_short")
  ) {
    return marketContextVariantTooltipText(candleMetricFromKind, slice.candle_direction, defs, tooltipOptions);
  }

  const rawPairSource = [label, display].find((s) => /[:=]/.test(s)) ?? "";
  if (rawPairSource) {
    const [kRaw, ...vRest] = rawPairSource.split(/[:=]/);
    const k = normalizeDefKey(kRaw);
    const v = normalizeDefKey(vRest.join(":"));
    if (k && v) {
      const pairText = marketContextVariantTooltipText(k, v, defs, tooltipOptions);
      if (pairText) return pairText;
    }
  }

  const directCandidates = [
    label,
    display,
    label.replace(/\s+\+\s+/g, "|"),
    display.replace(/\s+\+\s+/g, "|"),
  ]
    .map(normalizeDefKey)
    .filter(Boolean);

  for (const key of directCandidates) {
    if (candleMetricFromKind && slice.candle_direction && key === normalizeDefKey(slice.candle_direction)) {
      const cdText = marketContextVariantTooltipText(candleMetricFromKind, slice.candle_direction, defs, tooltipOptions);
      if (cdText) return cdText;
    }
    if (defs[key] != null) return ensureSentence(toNaturalMetricDefinition(defs[key]));
  }

  const parts = display
    .split(/\s*\+\s*|\s*\|\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return "";

  const definedParts = parts
    .map((part) => {
      const token = normalizeDefKey(part);
      if (!token) return "";
      if (
        candleMetricFromKind &&
        slice.candle_direction &&
        token === normalizeDefKey(slice.candle_direction)
      ) {
        return marketContextVariantTooltipText(candleMetricFromKind, slice.candle_direction, defs, tooltipOptions);
      }
      if (slice.market_condition && token === normalizeDefKey(slice.market_condition)) {
        const regimeText = marketContextVariantTooltipText("regime", slice.market_condition, defs, tooltipOptions);
        if (regimeText) return regimeText;
      }
      if (slice.direction && token === normalizeDefKey(slice.direction)) {
        const sideText = marketContextVariantTooltipText("trade_direction", slice.direction, defs, tooltipOptions);
        if (sideText) return sideText;
      }
      const inferredText =
        token === "up" || token === "down" || token === "flat"
          ? marketContextVariantTooltipText("candle_direction", token, defs, tooltipOptions)
          : token === "long" || token === "short"
            ? marketContextVariantTooltipText("trade_direction", token, defs, tooltipOptions)
            : marketContextVariantTooltipText("regime", token, defs, tooltipOptions);
      if (inferredText && !inferredText.startsWith("This slice matches")) return inferredText;
      if (defs[token] != null) return ensureSentence(toNaturalMetricDefinition(defs[token]));
      return "";
    })
    .filter(Boolean);
  if (definedParts.length) return definedParts.join(" ");

  const single = normalizeDefKey(display);
  if (single && defs[single] != null) return ensureSentence(toNaturalMetricDefinition(defs[single]));
  return "";
}

function situationDefinitionTooltip(
  text: string,
  metricDefinitions?: Record<string, unknown>,
  context?: "best" | "worst",
): string {
  return situationBriefTooltip(text, metricDefinitions, context);
}

function sliceCategorySortKey(slice: ConditionAnalysisSlice, candleTimeframe?: string | null): string {
  if (slice.kind === "all") return "\u0000";
  return `${sliceKindToDrilldownTableKey(slice.kind) || slice.kind}|${getSliceCategoryLabel(slice.kind, candleTimeframe)}`;
}

function createMarketContextPivotColumns(
  sliceHeadline: SliceHeadlineFn,
  headlineOpts: { sessionMarginalSourceNode?: ConditionAnalysisNode | null },
  metricDefinitions?: Record<string, unknown>,
  opts?: {
    candleTimeframe?: string | null;
    onPreviewSlice?: (slice: ConditionAnalysisSlice) => void;
    previewActiveKey?: string | readonly string[];
    previewLoadingKey?: string | readonly string[];
    onPickSlice?: (slice: ConditionAnalysisSlice) => void;
    isSlicePicked?: (slice: ConditionAnalysisSlice) => boolean;
    onOpenSlice?: (slice: ConditionAnalysisSlice) => void;
  },
): ColumnDef<ConditionAnalysisSlice>[] {
  const headline = (slice: ConditionAnalysisSlice) => sliceHeadline(slice, headlineOpts);
  const showPickColumn = Boolean(opts?.onPickSlice);
  const showOpenColumn = Boolean(opts?.onOpenSlice);
  const candleTimeframe = opts?.candleTimeframe;

  const cols: ColumnDef<ConditionAnalysisSlice>[] = [
    {
      id: "slice",
      accessorFn: (row) => getDisplaySliceName(row, candleTimeframe),
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Slice" align="left" />
      ),
      cell: ({ row }) => {
        const label = getDisplaySliceName(row.original, candleTimeframe);
        const tooltipText =
          sliceDefinitionTooltip(row.original, metricDefinitions, candleTimeframe) ||
          `Shows results for the “${label}” slice path.`;
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            className="min-w-0 whitespace-normal break-words font-medium"
          >
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-no-row-click=""
                    className="cursor-help underline decoration-dotted underline-offset-2"
                  >
                    {label}
                  </span>
                </TooltipTrigger>
                <MarketContextTooltipContent widthClass="max-w-[360px]">
                  {tooltipText}
                </MarketContextTooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "category",
      accessorFn: (row) => sliceCategorySortKey(row, candleTimeframe),
      sortingFn: (rowA, rowB) => {
        const a = sliceKindToDrilldownTableKey(rowA.original.kind) || rowA.original.kind;
        const b = sliceKindToDrilldownTableKey(rowB.original.kind) || rowB.original.kind;
        if (rowA.original.kind === "all" && rowB.original.kind !== "all") return -1;
        if (rowB.original.kind === "all" && rowA.original.kind !== "all") return 1;
        const byKey = compareRegimeDrilldownTableKeys(a, b);
        if (byKey !== 0) return byKey;
        return getDisplaySliceName(rowA.original, candleTimeframe).localeCompare(
          getDisplaySliceName(rowB.original, candleTimeframe),
          undefined,
          {
            sensitivity: "base",
          },
        );
      },
      header: ({ column }) => (
        <DataTableColumnHeader<ConditionAnalysisSlice, unknown> column={column} title="Category" />
      ),
      cell: ({ row }) => (
        <MarketContextSliceCategoryBadge slice={row.original} candleTimeframe={candleTimeframe} />
      ),
    },
  ];

  if (showPickColumn) {
    cols.push({
      id: "simulatorPick",
      enableSorting: false,
      header: () => <AddToSimulatorColumnHeader />,
      cell: ({ row }) => {
        const slice = row.original;
        if (slice.kind === "all") {
          return (
            <MarketContextActionColumnCell>
              <span className="text-sm text-muted-foreground">—</span>
            </MarketContextActionColumnCell>
          );
        }
        const picked = Boolean(opts?.isSlicePicked?.(slice));
        return (
          <MarketContextActionColumnCell>
            <div data-market-context-simulator-pick-cell="">
              <SimulatorPickActionCell
                picked={picked}
                addAriaLabel="Add to Simulator"
                onAdd={() => opts?.onPickSlice?.(slice)}
              />
            </div>
          </MarketContextActionColumnCell>
        );
      },
    });
  }

  cols.push(
    {
      id: "sample",
      accessorFn: (row) => headline(row).sample ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Sample"
          tooltip="All trades in the slice row, including scratch/breakeven."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtInt(headline(row.original).sample)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "winRate",
      accessorFn: (row) => headline(row).winRate ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Win rate" tooltip={WIN_RATE_COLUMN_TOOLTIP} />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtPct(headline(row.original).winRate)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "expectancy",
      accessorFn: (row) => headline(row).expectancy ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Expectancy"
          tooltip="Average expected return per trade for the slice."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtNum(headline(row.original).expectancy)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "totalPnl",
      accessorFn: (row) => headline(row).totalPnl ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Total PnL"
          tooltip="Total profit and loss across all trades in the slice."
        />
      ),
      cell: ({ row }) => {
        const totalPnl = headline(row.original).totalPnl;
        if (totalPnl == null || !Number.isFinite(totalPnl)) {
          return (
            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(totalPnl) }}>
              {fmtPnl(totalPnl)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "best",
      accessorFn: (row) => headline(row).bestSituation ?? "",
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Top best situation"
          tooltip="Best-performing situation example for the selected slice."
          align="left"
        />
      ),
      cell: ({ row }) => {
        const text = headline(row.original).bestSituation ?? "—";
        const tooltipText = situationDefinitionTooltip(text, metricDefinitions, "best");
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            data-market-context-situation-cell=""
            className="min-w-0 whitespace-normal break-words text-sm"
            title={text === "—" ? undefined : text}
          >
            {tooltipText ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="line-clamp-2 cursor-help underline decoration-dotted underline-offset-2"
                      data-no-row-click=""
                    >
                      {text}
                    </span>
                  </TooltipTrigger>
                  <MarketContextTooltipContent widthClass="max-w-[420px]">
                    {tooltipText}
                  </MarketContextTooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="line-clamp-2">{text}</span>
            )}
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "worst",
      accessorFn: (row) => headline(row).worstSituation ?? "",
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Top worst situation"
          tooltip="Worst-performing situation example for the selected slice."
          align="left"
        />
      ),
      cell: ({ row }) => {
        const text = headline(row.original).worstSituation ?? "—";
        const tooltipText = situationDefinitionTooltip(text, metricDefinitions, "worst");
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            data-market-context-situation-cell=""
            className="min-w-0 whitespace-normal break-words text-sm"
            title={text === "—" ? undefined : text}
          >
            {tooltipText ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="line-clamp-2 cursor-help underline decoration-dotted underline-offset-2"
                      data-no-row-click=""
                    >
                      {text}
                    </span>
                  </TooltipTrigger>
                  <MarketContextTooltipContent widthClass="max-w-[420px]">
                    {tooltipText}
                  </MarketContextTooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="line-clamp-2">{text}</span>
            )}
          </TradeLogSortableCell>
        );
      },
    },
  );

  if (showOpenColumn || opts?.onPreviewSlice) {
    cols.push({
      id: "actions",
      enableSorting: false,
      header: () => <MarketContextActionColumnHeader>Actions</MarketContextActionColumnHeader>,
      cell: ({ row }) => {
        const slice = row.original;
        const rowKey = `configured-slice:${slice.id}`;
        const sample = headline(slice).sample;
        const previewDisabled =
          slice.kind === "all" || (typeof sample === "number" && sample <= 0);
        const previewActive = marketContextPreviewKeyMatches(opts?.previewActiveKey, rowKey);
        const previewLoading = marketContextPreviewKeyMatches(opts?.previewLoadingKey, rowKey);
        const canOpen = showOpenColumn && slice.kind !== "all";
        const showPreview = Boolean(opts?.onPreviewSlice);

        if (!canOpen && !showPreview) {
          return (
            <MarketContextActionColumnCell>
              <span className="text-sm text-muted-foreground">—</span>
            </MarketContextActionColumnCell>
          );
        }

        if (slice.kind === "all" && !showPreview) {
          return (
            <MarketContextActionColumnCell>
              <span className="text-sm text-muted-foreground">—</span>
            </MarketContextActionColumnCell>
          );
        }

        return (
          <MarketContextActionColumnCell>
            <TooltipProvider delayDuration={350}>
              <div
                data-market-context-slice-actions=""
                className="inline-flex flex-nowrap items-center gap-2"
              >
                {canOpen ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="gradient"
                        size="sm"
                        data-font="primary"
                        data-no-row-click=""
                        className="h-8 shrink-0 px-4 normal-case tracking-normal active:translate-y-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          opts?.onOpenSlice?.(slice);
                        }}
                      >
                        Open
                      </Button>
                    </TooltipTrigger>
                    <MarketContextTooltipContent widthClass="max-w-[280px]">
                      {OPEN_SLICE_ROW_TOOLTIP}
                    </MarketContextTooltipContent>
                  </Tooltip>
                ) : null}
                {showPreview ? (
                  <Button
                    type="button"
                    variant={previewActive && !previewLoading ? "outline-green" : "outline-brand"}
                    size="sm"
                    data-font="primary"
                    data-no-row-click=""
                    data-market-context-preview-active={previewActive && !previewLoading ? "" : undefined}
                    className="h-8 shrink-0 px-4 normal-case tracking-normal active:translate-y-0"
                    disabled={previewDisabled || previewLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      opts?.onPreviewSlice?.(slice);
                    }}
                  >
                    {previewLoading ? "…" : previewActive ? "Active" : "Preview"}
                  </Button>
                ) : null}
              </div>
            </TooltipProvider>
          </MarketContextActionColumnCell>
        );
      },
    });
  }

  return cols;
}

export type MarketContextPivotTableProps = {
  slices: ConditionAnalysisSlice[];
  selectedId: string;
  /** Highlight + seed Slice configurator from this row (All trades clears fields). */
  onSelect: (slice: ConditionAnalysisSlice) => void;
  sliceHeadline: SliceHeadlineFn;
  metricDefinitions?: Record<string, unknown>;
  sessionMarginalSourceNode?: ConditionAnalysisNode | null;
  /** Report candle TF — scales long/short direction bar-window labels. */
  candleTimeframe?: string | null;
  /** When true, omit outer card chrome (parent panel provides the border). */
  embedded?: boolean;
  title?: string;
  description?: string;
  /** Overview deep-link — scroll to and pulse the selected slice row. */
  evidenceFocusNonce?: number;
  onPreviewSlice?: (slice: ConditionAnalysisSlice) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  onPickSlice?: (slice: ConditionAnalysisSlice) => void;
  isSlicePicked?: (slice: ConditionAnalysisSlice) => boolean;
  onOpenSlice?: (slice: ConditionAnalysisSlice) => void;
  /** Hide collapse / “Show all” row under the table (demo report). */
  hideSliceCollapseControls?: boolean;
};

export function MarketContextPivotTable({
  slices,
  selectedId,
  onSelect,
  sliceHeadline,
  metricDefinitions,
  sessionMarginalSourceNode,
  candleTimeframe,
  embedded = false,
  title = "Slice exploration",
  description = "All slice categories in one table, including the main single-dimension slices. Rows start grouped by category — sort any column to reorder across the full set.",
  evidenceFocusNonce,
  onPreviewSlice,
  previewActiveKey,
  previewLoadingKey,
  onPickSlice,
  isSlicePicked,
  onOpenSlice,
  hideSliceCollapseControls = false,
}: MarketContextPivotTableProps): React.ReactElement {
  const headlineOpts = useMemo(
    () => ({ sessionMarginalSourceNode }),
    [sessionMarginalSourceNode],
  );

  const [sorting, setSorting] = useState<SortingState>([{ id: "category", desc: false }]);

  const columns = useMemo(
    () =>
      createMarketContextPivotColumns(sliceHeadline, headlineOpts, metricDefinitions, {
        candleTimeframe,
        onPreviewSlice,
        previewActiveKey,
        previewLoadingKey,
        onPickSlice,
        isSlicePicked,
        onOpenSlice,
      }),
    [
      candleTimeframe,
      headlineOpts,
      isSlicePicked,
      metricDefinitions,
      onOpenSlice,
      onPickSlice,
      onPreviewSlice,
      previewActiveKey,
      previewLoadingKey,
      sliceHeadline,
    ],
  );

  useLayoutEffect(() => {
    if (!evidenceFocusNonce || !selectedId) return;
    return scheduleScrollMarketContextRow({
      containerAttr: "data-market-context-pivot-table",
      rowSelector: marketContextActiveRowSelector(),
      maxAttempts: 28,
      attemptMs: 75,
    });
  }, [evidenceFocusNonce, selectedId, slices.length]);

  const needsCollapse = slices.length > MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT;
  const [viewportExpanded, setViewportExpanded] = useState(false);

  React.useEffect(() => {
    setViewportExpanded(false);
  }, [slices.length, title]);

  const fieldHeader =
    title || description ? (
      <Field className="mb-4 w-full">
        {title ? <FieldLabel className={MARKET_CONTEXT_FIELD_LABEL_CLASS}>{title}</FieldLabel> : null}
        {description ? (
          <FieldDescription className={MARKET_CONTEXT_FIELD_DESC_CLASS}>{description}</FieldDescription>
        ) : null}
      </Field>
    ) : null;

  const table = (
    <div data-market-context-pivot-table-body="" className="w-full min-w-0">
      <div
        data-market-context-pivot-scroll=""
        className="rounded-[6px] border border-border bg-background"
      >
        <div
          data-market-context-pivot-scroll-inner=""
          className={
            needsCollapse && !viewportExpanded
              ? "max-h-[calc(2.5rem+2.5rem*var(--market-context-pivot-visible-rows))] min-h-[10rem] resize-y overflow-x-auto overflow-y-auto overscroll-y-contain [&_[data-slot=table-head]]:sticky [&_[data-slot=table-head]]:top-0 [&_[data-slot=table-head]]:z-[1] [&_[data-slot=table-head]]:bg-background [&_[data-slot=table-header]_[data-slot=table-row]]:shadow-[inset_0_-1px_0_0_var(--border)]"
              : needsCollapse
                ? "max-h-[min(80vh,56rem)] overflow-x-auto overflow-y-auto overscroll-y-contain [&_[data-slot=table-head]]:sticky [&_[data-slot=table-head]]:top-0 [&_[data-slot=table-head]]:z-[1] [&_[data-slot=table-head]]:bg-background [&_[data-slot=table-header]_[data-slot=table-row]]:shadow-[inset_0_-1px_0_0_var(--border)]"
                : "overflow-x-auto"
          }
          style={
            needsCollapse && !viewportExpanded
              ? ({
                  ["--market-context-pivot-visible-rows" as string]: String(
                    MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT,
                  ),
                  height: `calc(2.5rem + 2.5rem * ${MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT})`,
                } as React.CSSProperties)
              : undefined
          }
        >
          <DataTable
            columns={columns}
            data={slices}
            getRowId={(row) => row.id}
            activeRowId={selectedId}
            evidencePulseNonce={evidenceFocusNonce}
            sorting={sorting}
            onSortingChange={setSorting}
            getRowDataAttributes={(row) =>
              evidenceFocusNonce && row.id === selectedId
                ? { "data-market-context-evidence-highlight": String(evidenceFocusNonce) }
                : undefined
            }
            onRowClick={(row) => onSelect(row)}
            showPagination={false}
            showTableBorder={false}
            pagination={{ pageIndex: 0, pageSize: Math.max(slices.length, 1) }}
            emptyMessage="No slices available."
            className="min-w-0"
          />
        </div>
      </div>
      {needsCollapse && !hideSliceCollapseControls ? (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {viewportExpanded
              ? `Showing all ${slices.length} slices.`
              : `Collapsed to about ${MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT} of ${slices.length} rows — drag the bottom-right corner to pull down, or expand.`}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            onClick={() => setViewportExpanded((open) => !open)}
          >
            {viewportExpanded ? "Collapse to top 10" : "Show all"}
          </Button>
        </div>
      ) : null}
    </div>
  );

  if (embedded) {
    return (
      <div
        data-market-context-pivot-table=""
        data-market-context-pivot-table-embedded=""
        className="mb-6 w-full min-w-0"
      >
        {fieldHeader}
        {table}
      </div>
    );
  }

  return (
    <div
      data-trade-log-trades-table=""
      data-market-context-pivot-table=""
      className="mb-6 flex w-full flex-col rounded-[6px] border border-[#d3d3d3] bg-card text-card-foreground shadow-sm p-4"
    >
      {fieldHeader}
      {table}
    </div>
  );
}
